import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring } from "framer-motion";

function Drone({ className }) {
    return (
        <svg viewBox="0 0 80 80" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <rect x="31" y="35" width="18" height="10" rx="3" />
            <path d="M31 38L19 26M49 38l12-12M31 42L19 54M49 42l12 12" />
            <circle cx="40" cy="40" r="2" fill="currentColor" stroke="none" />
            {[
                [19, 26],
                [61, 26],
                [19, 54],
                [61, 54],
            ].map(([cx, cy]) => (
                <g key={`${cx}-${cy}`} className="drone-rotor" style={{ transformOrigin: `${cx}px ${cy}px` }}>
                    <ellipse cx={cx} cy={cy} rx="12" ry="2.6" opacity="0.9" />
                    <ellipse cx={cx} cy={cy} rx="2.6" ry="12" opacity="0.35" />
                </g>
            ))}
        </svg>
    );
}

function CameraFinder() {
    return (
        <div className="relative h-[88px] w-[88px]">
            <svg viewBox="0 0 88 88" className="finder-spin absolute inset-0 h-full w-full text-[#121211]" fill="none" stroke="currentColor" strokeWidth="1">
                <circle cx="44" cy="44" r="42" strokeDasharray="2 7" opacity="0.6" />
            </svg>
            <svg viewBox="0 0 88 88" className="absolute inset-0 h-full w-full text-[#121211]" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M24 33v-9h9M64 33v-9h-9M24 55v9h9M64 55v9h-9" />
                <path d="M44 38v-5M44 55v-5M38 44h-5M55 44h-5" opacity="0.7" />
                <circle cx="44" cy="44" r="1.8" fill="currentColor" stroke="none" />
            </svg>
            <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 font-mono2 text-[9px] tracking-[0.3em] text-[#121211]/70">
                360°
            </span>
        </div>
    );
}

export default function ServiceCursor({ mode }) {
    const [fine, setFine] = useState(false);
    const [active, setActive] = useState(false);
    const x = useMotionValue(-200);
    const y = useMotionValue(-200);
    const sx = useSpring(x, { stiffness: 420, damping: 38, mass: 0.6 });
    const sy = useSpring(y, { stiffness: 420, damping: 38, mass: 0.6 });

    useEffect(() => {
        const mq = window.matchMedia("(pointer: fine)");
        setFine(mq.matches);
        if (!mq.matches) return undefined;
        const move = (e) => {
            x.set(e.clientX);
            y.set(e.clientY);
            setActive(!!e.target.closest?.("button, a, input, textarea, select, [role='button'], [data-interactive]"));
        };
        window.addEventListener("mousemove", move, { passive: true });
        return () => window.removeEventListener("mousemove", move);
    }, [x, y]);

    if (!fine) return null;

    return (
        <motion.div
            className="pointer-events-none fixed left-0 top-0 z-40"
            style={{ x: sx, y: sy, opacity: active ? 0 : 1, pointerEvents: "none" }}
            aria-hidden="true"
            data-testid="service-cursor"
            data-mode={mode || "none"}
            data-active={active}
        >
            <motion.div
                animate={{ scale: active && mode ? 1 : 0, opacity: active && mode ? 1 : 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="absolute left-0 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#121211]"
                data-testid="service-cursor-dot"
            />
            <AnimatePresence mode="wait">
                {mode === "camera" && (
                    <motion.div
                        key="camera"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: active ? 0.55 : 1, scale: active ? 0.55 : 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="-translate-x-1/2 -translate-y-1/2"
                        data-testid="service-cursor-camera"
                    >
                        <CameraFinder />
                    </motion.div>
                )}
                {mode === "drone" && (
                    <motion.div
                        key="drone"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: active ? 0.55 : 1, scale: active ? 0.55 : 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="relative -translate-x-1/2 -translate-y-1/2"
                        data-testid="service-cursor-drone"
                    >
                        <Drone className="drone-shadow absolute left-0 top-0 h-[88px] w-[88px] text-black" />
                        <div className="drone-float">
                            <Drone className="h-[88px] w-[88px] text-[#121211]" />
                        </div>
                        <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 font-mono2 text-[9px] tracking-[0.3em] text-[#121211]/70">
                            AERIAL
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
